import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Check, AlertCircle, Phone, Loader2, Wifi } from 'lucide-react';
import { toast } from 'sonner';
import DirectCall from '../utils/DirectCall';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const MobilePair = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('connecting');
  const [socketConnected, setSocketConnected] = useState(false);
  const sessionId = searchParams.get('session');
  const wsRef = useRef(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      toast.error('Invalid pairing link');
      return;
    }

    localStorage.setItem('mobile_session_id', sessionId);
    connectWebSocket(sessionId);

    return () => {};
  }, [sessionId]);

  const connectWebSocket = (session) => {
    const wsUrl = BACKEND_URL.replace('https://', 'wss://').replace('http://', 'ws://');
    const fullUrl = `${wsUrl}/api/ws/mobile/${session}`;
    
    const ws = new WebSocket(fullUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      setSocketConnected(true);
      setStatus('paired');
      toast.success('Device paired!');
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'dial_number') {
          toast.info(`Calling ${data.customer_name}...`);
          DirectCall.call(data.phone);
        }
      } catch (e) {
        console.error('Failed to parse message:', e);
      }
    };

    ws.onclose = () => {
      setSocketConnected(false);
    };

    ws.onerror = () => {
      setStatus('error');
    };
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-sm p-6 bg-white shadow-sm">
        {status === 'connecting' && (
          <div className="text-center">
            <Loader2 className="h-12 w-12 mx-auto text-blue-600 animate-spin mb-4" />
            <h2 className="text-lg font-semibold text-slate-900">Connecting...</h2>
            <p className="text-sm text-slate-500 mt-1">Please wait</p>
          </div>
        )}

        {status === 'paired' && (
          <div className="text-center">
            <div className="h-14 w-14 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-4">
              <Check className="h-7 w-7 text-green-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Connected!</h2>
            <p className="text-sm text-slate-500 mt-1 mb-4">Ready to receive calls</p>

            <div className="flex items-center justify-center gap-2 mb-4">
              <Wifi className={`h-4 w-4 ${socketConnected ? 'text-green-600' : 'text-yellow-600'}`} />
              <span className={`text-sm ${socketConnected ? 'text-green-600' : 'text-yellow-600'}`}>
                {socketConnected ? 'Online' : 'Reconnecting...'}
              </span>
            </div>

            <div className="bg-green-50 p-3 rounded-lg mb-4">
              <div className="flex items-center justify-center gap-2 text-green-700">
                <Phone className="h-4 w-4" />
                <span className="text-sm font-medium">Click-to-Call Active</span>
              </div>
            </div>

            <Button 
              onClick={() => navigate('/mobile')}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white"
            >
              Go to Home
            </Button>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center">
            <div className="h-12 w-12 mx-auto rounded-full bg-red-50 flex items-center justify-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600" />
            </div>
            <h2 className="text-lg font-semibold text-slate-900">Connection Failed</h2>
            <p className="text-sm text-slate-500 mt-1 mb-4">
              {sessionId ? 'Could not connect to CRM' : 'Invalid pairing link'}
            </p>
            <Button 
              onClick={() => navigate('/mobile')}
              variant="outline"
              className="w-full"
            >
              Try Again
            </Button>
          </div>
        )}
      </Card>
    </div>
  );
};

export default MobilePair;
